import { useModeAwareQuery, useModeAwareMutation } from "./useModeAwareQuery";
import { useReceiptByIdGraphQL } from "./useReceiptsGraphQL";
import { useApi } from "./useApi";
import { generateMockTransactions } from "@/data/payments-mock-data";
import type { Transaction, TransactionStatus } from "@/types/payments";
import type { TransactionApi } from "@/types/api/transactions";
import type { CreateAllocationsRequest, CreateAllocationsResponseApi } from "@/types/api/allocations";

// Map API transaction to UI transaction type
function mapTransactionApiToUi(apiTransaction: TransactionApi, homeCurrency: string): Transaction {
  const dueDate = apiTransaction.dueDate ? new Date(apiTransaction.dueDate) : new Date();
  const now = new Date();
  const daysOverdue = Math.max(0, Math.floor((now.getTime() - dueDate.getTime()) / (1000 * 60 * 60 * 24)));
  const balance = apiTransaction.outstandingAmount;
  const status: TransactionStatus = balance <= 0 ? 'completed' : daysOverdue > 0 ? 'overdue' : 'outstanding';

  const isNonHomeCurrency = apiTransaction.currency !== homeCurrency && apiTransaction.currencyAmount != null;

  return {
    id: apiTransaction.id,
    transactionNumber: apiTransaction.referenceNumber,
    transactionType: apiTransaction.transactionType.toLowerCase() as Transaction["transactionType"],
    customerId: apiTransaction.customerId,
    customerName: apiTransaction.customerName,
    transactionDate: new Date(apiTransaction.transactionDate),
    dueDate,
    originalAmount: isNonHomeCurrency ? apiTransaction.currencyAmount! : apiTransaction.amount,
    currentBalance: isNonHomeCurrency ? (apiTransaction.currencyOutstandingAmount ?? 0) : balance,
    currency: apiTransaction.currency,
    homeCurrencyAmount: isNonHomeCurrency ? apiTransaction.amount : undefined,
    homeCurrencyBalance: isNonHomeCurrency ? balance : undefined,
    status,
    daysOverdue,
    onDispute: apiTransaction.onDispute,
    isPromised: false,
  };
}

/**
 * Hook to fetch a single receipt by ID for allocation
 * Handles both Demo and Live modes via GraphQL
 */
export function useReceiptForAllocation(receiptId: number | undefined, homeCurrency?: string) {
  return useReceiptByIdGraphQL(receiptId, homeCurrency);
}

/**
 * Hook to fetch transactions for a customer (for allocation)
 * Handles both Demo and Live modes
 */
export function useTransactionsForAllocation(customerId: number | undefined, homeCurrency: string) {
  const { apiGet } = useApi();
  const mockTransactions = customerId ? generateMockTransactions(customerId) : [];

  return useModeAwareQuery<Transaction[], Error>(
    ["transactions-for-allocation", customerId],
    async ({ signal }) => {
      if (!customerId) return [];
      const response = await apiGet<{ data: TransactionApi[] }>(
        "/api/v1/transactions",
        { customerId: String(customerId), pageSize: 100 },
        signal
      );
      // Filter to only invoices with outstanding balance
      return response.data
        .filter(x => x.transactionType.toLowerCase() === "invoice" && x.outstandingAmount > 0)
        .map(x => mapTransactionApiToUi(x, homeCurrency));
    },
    mockTransactions.filter(t => t.transactionType === "invoice" && t.currentBalance > 0),
    {
      enabled: !!customerId,
      staleTime: 0,
      refetchOnMount: 'always',
    }
  );
}

/**
 * Hook to create allocations
 * Handles both Demo and Live modes
 */
export function useCreateAllocations() {
  const { apiPost } = useApi();

  return useModeAwareMutation<CreateAllocationsResponseApi, Error, CreateAllocationsRequest>(
    async (request) => {
      return apiPost<CreateAllocationsResponseApi>(
        "/api/v1/allocations",
        request
      );
    },
    async (request) => {
      // Mock response for demo mode
      return {
        receiptId: request.receiptId,
        allocations: request.allocations.map((a, index) => ({
          id: Date.now() + index,
          receiptId: request.receiptId,
          transactionId: a.transactionId,
          allocatedAmount: a.allocatedAmount,
          allocationDate: new Date().toISOString(),
          allocationMethod: a.allocationMethod,
          notes: a.notes,
          createdBy: "demo-user",
          createdAt: new Date().toISOString(),
        })),
        receiptSummary: {
          receivedAmount: 0,
          allocatedAmount: request.allocations.reduce((sum, a) => sum + a.allocatedAmount, 0),
          unappliedAmount: 0,
          allocationStatus: "fully_allocated",
        },
      };
    }
  );
}

/**
 * Combined hook for allocation workspace data
 * Returns receipt, transactions, and mutation for creating allocations
 */
export function useAllocationWorkspaceData(receiptId: number | undefined, homeCurrency: string) {
  const receiptQuery = useReceiptForAllocation(receiptId, homeCurrency);
  const customerId = receiptQuery.data?.customerId;
  const transactionsQuery = useTransactionsForAllocation(customerId, homeCurrency);
  const createAllocationsMutation = useCreateAllocations();

  return {
    receipt: receiptQuery.data,
    transactions: transactionsQuery.data ?? [],
    isLoading: receiptQuery.isLoading || (receiptQuery.data && transactionsQuery.isLoading),
    isLoadingReceipt: receiptQuery.isLoading,
    isLoadingTransactions: transactionsQuery.isLoading,
    error: receiptQuery.error || transactionsQuery.error,
    createAllocations: createAllocationsMutation.mutateAsync,
    isSubmitting: createAllocationsMutation.isPending,
    refetch: () => {
      receiptQuery.refetch();
      transactionsQuery.refetch();
    },
  };
}
